import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Input, Modal, Row, Select, Spin } from 'antd';
import type { FC } from 'react';
import { NAMESPACE } from '@/constants';
import type { UserFormProps } from './UserForm.types';
import { Title } from '@/components';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { StyledTitle } from './UserForm.styles';
import { FORM_FIELD } from './UserForm.constants';
import { useUserForm } from './hooks/useUserForm';
import { RoleDetails } from '../../UserSettings.constants';
import { USER_ROLES } from '@/constants/roles';

const { Item } = Form;
const { Option } = Select;

const UserForm: FC<Omit<UserFormProps, 'isOpen'>> = ({ user, close }) => {
  const { t } = useTranslation(NAMESPACE.USER_SETTINGS_PAGE);

  const { config, form, handleSubmit, handleValuesChange, isLoading, isFormTouched } = useUserForm({
    user,
    close
  });

  return (
    <Spin spinning={isLoading} size="large">
      <StyledTitle level={5}>{t('addUserInfo')}</StyledTitle>
      <Form
        form={form}
        initialValues={user}
        onValuesChange={handleValuesChange}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Row gutter={24}>
          <Col span={12}>
            <Item {...config[FORM_FIELD.FIRST_NAME]}>
              <Input size="large" placeholder={t('inputFirstName') as string} />
            </Item>
          </Col>
          <Col span={12}>
            <Item {...config[FORM_FIELD.LAST_NAME]}>
              <Input size="large" placeholder={t('inputLastName') as string} />
            </Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Item {...config[FORM_FIELD.EMAIL]}>
              <Input disabled={!!user} size="large" type="email" placeholder={t('inputEmail') as string} />
            </Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Item {...config[FORM_FIELD.ROLE]}>
              <Select size="large" placeholder={t('selectRole')}>
                {USER_ROLES.map((role) => (
                  <Option key={role} value={role}>
                    {RoleDetails[role].name}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
        </Row>
        <Row justify="end">
          <Button disabled={!isFormTouched || isLoading} size="large" htmlType="submit" type="primary">
            {user ? t('save', { ns: NAMESPACE.COMMON }) : t('create', { ns: NAMESPACE.COMMON })}
          </Button>
        </Row>
      </Form>
    </Spin>
  );
};

export default ({ close, isOpen, user }: UserFormProps) => {
  const { t } = useTranslation(NAMESPACE.USER_SETTINGS_PAGE);
  const title = user ? t('editUser') : t('createUser');
  const modalContent = user ? t('cancelUserEditingProcess') : t('cancelUserCreationProcess');
  const { handleConfirm } = useConfirmModal({
    onCancel: close,
    title: t('cancelModalTitle'),
    content: modalContent,
    okText: t('cancel', { ns: NAMESPACE.COMMON }),
    cancelText: t('close', { ns: NAMESPACE.COMMON })
  });

  return (
    <Modal
      footer={false}
      destroyOnHidden
      title={<Title level={4}>{title}</Title>}
      open={isOpen}
      width={800}
      onCancel={handleConfirm}
    >
      <UserForm close={close} user={user} />
    </Modal>
  );
};
